import React, { useState, useEffect } from 'react';
import withDataWrapper from 'FieldServices/hooks/withDataWrapper';
import { CreateExtComponent } from '@powerednow/client-shared/Components/react/React';
import { ComplexModelInstance } from '@powerednow/models/modelLookup';
import BaseDialog from '@powerednow/components/Dialog/Dialog';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { DOCUMENT } from '@powerednow/shared/constants';
import Select from '@powerednow/components/fields/Select';
import type { SelectChangeEvent } from '@mui/material';
import Fieldset from '@powerednow/components/fields/Fieldset';
import Optional from '@powerednow/components/fields/Optional';

type DocumentConvertProps = {
    sourceDocument: ComplexModelInstance<'Document'>
    typeNameMap: Record<keyof typeof DOCUMENT.TYPES, string>
    permissionMap: Record<keyof typeof DOCUMENT.TYPES, boolean>
    onClose: () => void
    onConvert: (targetType: number) => Promise<void>
}

const useStyles = makeStyles<Theme>(createStyles({
    root: {
        '& .MuiDialogContent-root': {
            display: 'flex',
        },
        '& .Fieldset-root': {
            flex: 1,
        },

    },
}));

function NoTargetDocument() {
    return (
        <div>
            <p>You do not have permission to convert this document.</p>
        </div>
    );
}

const OptionalNoTargetDocument = Optional(NoTargetDocument);

export function DocumentConvert({
    sourceDocument,
    typeNameMap,
    permissionMap,
    onClose,
    onConvert,
}: DocumentConvertProps) {
    const typesToConvert = DOCUMENT.CONVERTABLE_DOCUMENT_TYPES.filter(type => type !== sourceDocument?.data.type && permissionMap[type]);

    const classes = useStyles();
    const [open, setOpen] = useState(true);
    const [targetType, setTargetType] = useState(typesToConvert[0]);
    const [isApproved, setIsApproved] = useState(false);

    useEffect(() => {
        setIsApproved(sourceDocument.data.type === DOCUMENT.TYPES_VALUES.PURCHASE_ORDER ? sourceDocument.isApproved() : true);
    }, [sourceDocument]);

    if (!sourceDocument) {
        return null;
    }

    const handleCloseClick = () => {
        setOpen(false);
        onClose();
    };

    const handleTargetChange = (event: SelectChangeEvent<typeof DOCUMENT.CONVERTABLE_DOCUMENT_TYPES[number]>) => {
        if (typeof event.target.value !== 'number') {
            return;
        }
        setTargetType(event.target.value);
    };

    const handleSaveClick = async () => {
        await onConvert(targetType);
    };

    return (
        <BaseDialog
            className={classes.root}
            open={open}
            onClose={handleCloseClick}
            title={`Convert ${sourceDocument?.data.displaynumber}`}
            showCloseButton={false}
            headerButtonsLeft={[{
                color: 'secondary',
                onClick: handleCloseClick,
                children: 'Close',
            }]}
            headerButtonsRight={[{
                color: 'primary',
                onClick: handleSaveClick,
                children: 'Convert',
                disabled: !isApproved,
            }]}
        >
            <OptionalNoTargetDocument
                renderIf={typesToConvert.length === 0}
                defaultElement={isApproved ? (
                    <Fieldset
                        fields={[
                            (
                                <Select
                                    label={`Convert ${typeNameMap[sourceDocument.data.type]} to`}
                                    fullWidth
                                    onChange={handleTargetChange}
                                    value={targetType}
                                    items={typesToConvert.map(type => ({ id: type, text: typeNameMap[type] }))}
                                />
                            ),
                        ]}
                    />
                ) : (
                    <Fieldset fields={[(
                        <div>
                            <p>Document is not approved. You cannot convert it.</p>
                            <p>To convert it, you need to approve the document first.</p>
                        </div>
                    )]}
                    />
                )}
            />

        </BaseDialog>
    );
}

export default CreateExtComponent('FieldServices.react.view.DocumentConvert', withDataWrapper(DocumentConvert));
